import {
  ApplyToAllIcon,
  ApplyToNextIcon,
  Bin,
  EllipsisMenu,
  EmptyStateButton,
  IconButton,
  Input,
  Typography,
} from '@weareopenr/component-library';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import styles from './VenueOpeningTimeFields.module.scss';
import { TDay } from '../../types/venue.types';
import { capitalise } from 'src/helpers/capitalise';
import { doOpeningTimesOverlap } from '../VenueOpeningTimeEditModal/utilities/doOpeningTimesOverlap/doOpeningTimesOverlap';
import { createNewFormOpeningTime } from '../VenueOpeningTimeEditModal/utilities/createNewFormOpeningTime/createNewFormOpeningTime';
import { TEditVenueOpeningTimes } from '../VenueOpeningTimeEditModal/types';
import { DAYS } from '../../constants';
import applyTimesToNextDay from './applyTimesToNextDay';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

export interface IVenueOpeningTimeProps {
  formFields: UseFormReturn<TEditVenueOpeningTimes>;
  day: TDay;
  onDelete?: (ids: string[]) => void;
}

export const VenueOpeningTimeFields = ({ formFields, day, onDelete }: IVenueOpeningTimeProps) => {
  const {
    register,
    setValue,
    control,
    getValues,
    formState: { errors },
  } = formFields;

  const { fields, append, remove } = useFieldArray({
    control,
    name: day,
  });

  const applyToNext = (dayUserSelects: TDay) => {
    onDelete(applyTimesToNextDay(dayUserSelects, DAYS[DAYS.indexOf(dayUserSelects) + 1], getValues, setValue));
  };

  const applyToAllDays = (dayUserSelects: TDay) => {
    const schedulesToDelete: string[] = [];
    DAYS.forEach((dayToApplyTo) => {
      // don't apply schedules to self
      if (dayUserSelects !== dayToApplyTo)
        applyTimesToNextDay(dayUserSelects, dayToApplyTo, getValues, setValue).forEach((id) =>
          schedulesToDelete.push(id),
        );
    });
    onDelete(schedulesToDelete);
  };

  return (
    <div className={styles['day-container']}>
      <div className={styles['left-column']}>
        <Typography size="medium" weight="semi-bold">
          {capitalise(day)}
        </Typography>
      </div>
      <div className={styles['right-column']}>
        {fields.map((field, index) => {
          return (
            <div key={field.id + index} className={styles['input-container']}>
              <div className={styles['input-row']} data-testid={`${day}-${index}-container`}>
                <Input
                  type="time"
                  label="Start time"
                  register={register(`${day}.${index}.start`, {
                    required: 'Start time is required',
                    validate: {
                      doTimesOverlap: (_t, openingTimes: TEditVenueOpeningTimes) => {
                        return doOpeningTimesOverlap(openingTimes[day])
                          ? 'Opening times on the same day can’t overlap'
                          : true;
                      },
                    },
                    deps: day,
                  })}
                  error={errors?.[day]?.[index]?.start?.message}
                />
                <div className={styles['input-divider']} />
                <Input
                  data-testid={`${day}.${index}.end`}
                  type="time"
                  label="End time"
                  register={register(`${day}.${index}.end`, {
                    required: 'End time is required',
                    validate: {
                      afterStartTime: (endTime: string, openingTimes: TEditVenueOpeningTimes) => {
                        const startTime = openingTimes?.[day]?.[index]?.start;
                        return endTime > startTime || 'End time must be later than start time';
                      },
                      doTimesOverlap: (_t, openingTimes: TEditVenueOpeningTimes) => {
                        return doOpeningTimesOverlap(openingTimes[day])
                          ? 'Opening times on the same day can’t overlap'
                          : true;
                      },
                    },
                    deps: day,
                  })}
                  error={errors?.[day]?.[index]?.end?.message}
                />
              </div>
              <IconButton
                onClick={() => {
                  if (field.openingTimeId && onDelete) {
                    onDelete([field.openingTimeId]);
                  }
                  remove(index);
                }}
                buttonStyle="ghost"
                icon={Bin}
                aria-label="Delete opening time"
              />
            </div>
          );
        })}
        <div className={styles['add-button']}>
          <EmptyStateButton
            label="+ Add hours"
            onClick={() => {
              append(createNewFormOpeningTime());
            }}
          />
        </div>
      </div>
      <div className={cx({ 'ellipsis-container': fields.length > 0 })}>
        <EllipsisMenu
          data-testid="menu-options-button"
          variant="ghost"
          options={
            day.toUpperCase() !== DAYS[6]
              ? [
                  {
                    id: 0,
                    name: 'Apply to Next',
                    icon: ApplyToNextIcon,
                    onOptionClick: () => applyToNext(day),
                  },
                  {
                    id: 1,
                    name: 'Apply to All',
                    icon: ApplyToAllIcon,
                    onOptionClick: () => applyToAllDays(day),
                  },
                ]
              : [
                  {
                    id: 0,
                    name: 'Apply to All',
                    icon: ApplyToAllIcon,
                    onOptionClick: () => applyToAllDays(day),
                  },
                ]
          }
        />
      </div>
    </div>
  );
};
